import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchPartnerClientLedger,
  fetchPartnerClientWalletV2,
  fetchPartnerDriverWalletV2,
  fetchPartnerWalletV2,
  listPartnerDriverWalletsV2,
  recordPartnerClientPayment,
  settlePartnerDriverWalletV2,
  topupPartnerDriverWalletV2,
  type PartnerDriverSettleKind,
  type PartnerDriverSettleScope,
} from "../lib/partnerWallets";
import { usePartnerSessionStore } from "../lib/partnerAuth";

function usePartnerId() {
  return usePartnerSessionStore((s) => s.session?.partnerId ?? null);
}

/** Consolidated partner wallet (v2) — balance + recent transactions. */
export function usePartnerWalletV2() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-wallet-v2", partnerId],
    enabled: !!partnerId,
    queryFn: () => fetchPartnerWalletV2(partnerId!),
    refetchInterval: 60_000,
  });
}

/** Client fee-wallet snapshot + dues/consolidation flags (D6 warnings). */
export function usePartnerClientWalletV2(tenantId: string | null) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-client-wallet-v2", partnerId, tenantId],
    enabled: !!partnerId && !!tenantId,
    queryFn: () => fetchPartnerClientWalletV2(partnerId!, tenantId!),
    refetchInterval: 60_000,
  });
}

/** 250: client⇄partner dues rail (delivery fees to pay + payments). */
export function usePartnerClientLedger(tenantId: string | null) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-client-ledger", partnerId, tenantId],
    enabled: !!partnerId && !!tenantId,
    queryFn: () => fetchPartnerClientLedger(partnerId!, tenantId!),
    refetchInterval: 60_000,
  });
}

/** 250: record a payment received from the client (reduces the balance to pay). */
export function useRecordPartnerClientPayment() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { tenantId: string; amount: number; note?: string }) =>
      recordPartnerClientPayment(partnerId!, vars.tenantId, vars.amount, vars.note),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ["partner-client-ledger", partnerId, vars.tenantId],
      });
    },
  });
}

/** Fleet driver wallet overview (v2 internal driver wallets). */
export function usePartnerDriverWalletsV2() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-driver-wallets-v2", partnerId],
    enabled: !!partnerId,
    queryFn: () => listPartnerDriverWalletsV2(partnerId!),
    refetchInterval: 60_000,
  });
}

/** Driver wallet detail — wallet + transactions + trip settlements. */
export function usePartnerDriverWalletV2(driverId: string | null) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-driver-wallet-v2", partnerId, driverId],
    enabled: !!partnerId && !!driverId,
    queryFn: () => fetchPartnerDriverWalletV2(partnerId!, driverId!),
    refetchInterval: 60_000,
  });
}

/** Cash handover / payout action; refreshes wallet + overview + fleet rails. */
export function useSettlePartnerDriverWalletV2() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      driverId: string;
      kind: PartnerDriverSettleKind;
      amount: number;
      note?: string;
      scope?: PartnerDriverSettleScope | null;
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return settlePartnerDriverWalletV2(
        partnerId,
        vars.driverId,
        vars.kind,
        vars.amount,
        vars.note,
        vars.scope
      );
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["partner-driver-wallet-v2"] });
      void qc.invalidateQueries({ queryKey: ["partner-driver-wallets-v2"] });
      void qc.invalidateQueries({ queryKey: ["partner-wallet-v2"] });
      void qc.invalidateQueries({
        queryKey: ["partner-driver-wallet-v2", partnerId, vars.driverId],
      });
    },
  });
}

/** Add credit (top-up) to a fleet driver wallet; refreshes wallet + fleet rails. */
export function useTopupPartnerDriverWalletV2() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { driverId: string; amount: number; note?: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return topupPartnerDriverWalletV2(
        partnerId,
        vars.driverId,
        vars.amount,
        vars.note
      );
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["partner-driver-wallet-v2"] });
      void qc.invalidateQueries({ queryKey: ["partner-driver-wallets-v2"] });
      void qc.invalidateQueries({ queryKey: ["partner-wallet-v2"] });
      void qc.invalidateQueries({
        queryKey: ["partner-driver-wallet-v2", partnerId, vars.driverId],
      });
    },
  });
}
